import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Maximize2, Minimize2, AlertTriangle } from "lucide-react";

// Interactive square cropper — CONTAIN by default (whole image visible, padded #0D0E0F),
// pinch/scroll to zoom, drag to position, an optional "Fill frame" toggle, and NO UPSCALE
// (a small source renders at its native size, never stretched). Touch-first. Produces the
// cropped WebP blob the user sees (WYSIWYG) plus a normalized-offset transform for re-crop.
//
// Props:
//   source: { dataUrl, width, height }  (browser-decodable, e.g. from /me/image/decode)
//   initialTransform?: { zoom, ox, oy, fill }  (ox/oy are offsets normalized by the frame)
//   label?: string
//   busy?: boolean
//   onSave(blob, transform)   onCancel()
const PANEL = "#0D0E0F";
const MAX_ZOOM = 6;
const OUT_MAX = 1024;
const SOFT_MIN = 128; // natural short side below this is warned as "may look soft"

export function ImageCropper({ source, initialTransform, label = "Position your image", busy = false, onSave, onCancel }) {
  const frameRef = useRef(null);
  const imgRef = useRef(null);
  const [F, setF] = useState(320);
  const [imgEl, setImgEl] = useState(null);
  const [fill, setFill] = useState(!!initialTransform?.fill);
  const [zoom, setZoom] = useState(initialTransform?.zoom || 1);
  const [offset, setOffset] = useState({ x: 0, y: 0 }); // display px, set after F known

  const W = source?.width || 1;
  const H = source?.height || 1;

  // Load the source into an Image so we can draw it to a canvas on save.
  useEffect(() => {
    if (!source?.dataUrl) return;
    const im = new Image();
    im.onload = () => setImgEl(im);
    im.src = source.dataUrl;
  }, [source?.dataUrl]);

  // Measure the rendered frame (responsive) so geometry uses the real size.
  useEffect(() => {
    const measure = () => { if (frameRef.current) setF(frameRef.current.clientWidth); };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const containScale = Math.min(F / W, F / H);
  const coverScale = Math.max(F / W, F / H);
  const baseScale = fill ? coverScale : containScale;
  const s = baseScale * zoom;                 // display px per source px
  const dispW = W * s;
  const dispH = H * s;

  const clampOffset = useCallback((o, curDispW, curDispH) => {
    const maxX = Math.abs(curDispW - F) / 2;
    const maxY = Math.abs(curDispH - F) / 2;
    return {
      x: Math.max(-maxX, Math.min(maxX, o.x)),
      y: Math.max(-maxY, Math.min(maxY, o.y)),
    };
  }, [F]);

  // Initialize / re-init offset when frame size or fill baseline changes.
  useEffect(() => {
    if (!F || !imgEl) return;
    setOffset((prev) => {
      // On first init, honor a stored normalized offset; otherwise center.
      const base = initialTransform && prev.x === 0 && prev.y === 0 && (initialTransform.ox || initialTransform.oy)
        ? { x: (initialTransform.ox || 0) * F, y: (initialTransform.oy || 0) * F }
        : prev;
      return clampOffset(base, dispW, dispH);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [F, imgEl, fill]);

  // ---- Pointer interaction (drag + pinch) ----
  const pointers = useRef(new Map());
  const gesture = useRef(null); // { startDist, startZoom, startMid, startOffset }

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const pts = [...pointers.current.values()];
      gesture.current = {
        startDist: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y),
        startZoom: zoom,
        startMid: { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 },
        startOffset: { ...offset },
      };
    }
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    const prev = pointers.current.get(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size >= 2 && gesture.current) {
      const pts = [...pointers.current.values()];
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      const nextZoom = Math.max(1, Math.min(MAX_ZOOM, gesture.current.startZoom * (dist / gesture.current.startDist)));
      const mid = { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
      const panX = mid.x - gesture.current.startMid.x;
      const panY = mid.y - gesture.current.startMid.y;
      setZoom(nextZoom);
      const nd = { w: W * baseScale * nextZoom, h: H * baseScale * nextZoom };
      setOffset(clampOffset({ x: gesture.current.startOffset.x + panX, y: gesture.current.startOffset.y + panY }, nd.w, nd.h));
      return;
    }
    // single-pointer drag
    const dx = e.clientX - prev.x;
    const dy = e.clientY - prev.y;
    setOffset((o) => clampOffset({ x: o.x + dx, y: o.y + dy }, dispW, dispH));
  };

  const endPointer = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) gesture.current = null;
  };

  const onWheel = (e) => {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.06 : 1 / 1.06;
    const nz = Math.max(1, Math.min(MAX_ZOOM, zoom * factor));
    setZoom(nz);
    const nd = { w: W * baseScale * nz, h: H * baseScale * nz };
    setOffset((o) => clampOffset(o, nd.w, nd.h));
  };

  // ---- WYSIWYG style for a frame of size P ----
  const styleFor = (P) => {
    const k = P / F;
    const iw = dispW * k;
    const ih = dispH * k;
    return {
      width: `${iw}px`, height: `${ih}px`,
      left: `${(P - iw) / 2 + offset.x * k}px`,
      top: `${(P - ih) / 2 + offset.y * k}px`,
      position: "absolute",
    };
  };

  const tooSmall = Math.min(W, H) < SOFT_MIN;

  const save = async () => {
    if (!imgEl) return;
    // outSize <= (F / s) guarantees the source is never upscaled (dest width <= natural width).
    const regionSrc = F / s;
    const outSize = Math.max(16, Math.min(Math.round(regionSrc), OUT_MAX));
    const k = outSize / F;                       // display px -> output px
    const canvas = document.createElement("canvas");
    canvas.width = outSize; canvas.height = outSize;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.fillStyle = PANEL;
    ctx.fillRect(0, 0, outSize, outSize);
    const iw = dispW * k, ih = dispH * k;
    const dx = (outSize - iw) / 2 + offset.x * k;
    const dy = (outSize - ih) / 2 + offset.y * k;
    ctx.drawImage(imgEl, 0, 0, W, H, dx, dy, iw, ih);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/webp", 0.9));
    const transform = { zoom, ox: offset.x / F, oy: offset.y / F, fill, nw: W, nh: H };
    onSave(blob, transform);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm"
      data-testid="image-cropper" onClick={busy ? undefined : onCancel}>
      <div className="bg-[#111213] border border-[#2E2F31] w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-lg uppercase tracking-tight text-[#E8E8EA] mb-3">{label}</h3>

        <div className="flex gap-4">
          {/* Crop frame */}
          <div
            ref={frameRef}
            data-testid="cropper-frame"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endPointer}
            onPointerCancel={endPointer}
            onWheel={onWheel}
            className="relative flex-1 aspect-square overflow-hidden border border-[#2E2F31] touch-none select-none cursor-grab active:cursor-grabbing"
            style={{ backgroundColor: PANEL }}
          >
            {imgEl ? (
              <img ref={imgRef} src={source.dataUrl} alt="" draggable={false} style={styleFor(F)} className="pointer-events-none max-w-none" />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-[#8B8D90]"><Loader2 className="w-6 h-6 animate-spin" /></div>
            )}
            <div className="absolute inset-0 pointer-events-none border border-white/10" />
          </div>

          {/* Live previews at real display sizes */}
          <div className="flex flex-col items-center gap-3 pt-1">
            <span className="text-[9px] font-mono uppercase tracking-wide text-[#6B6D70]">Actual size</span>
            {[44, 34].map((P) => (
              <div key={P} className="flex flex-col items-center gap-1">
                <div className="relative overflow-hidden rounded-full border border-[#2E2F31]"
                  style={{ width: P, height: P, backgroundColor: PANEL }} data-testid={`cropper-preview-${P}`}>
                  {imgEl && <img src={source.dataUrl} alt="" draggable={false} style={styleFor(P)} className="pointer-events-none max-w-none" />}
                </div>
                <span className="text-[8px] font-mono text-[#6B6D70]">{P}px</span>
              </div>
            ))}
          </div>
        </div>

        {/* Zoom + fill controls */}
        <div className="mt-4 flex items-center gap-3">
          <input
            type="range" min="1" max={MAX_ZOOM} step="0.01" value={zoom}
            onChange={(e) => { const nz = parseFloat(e.target.value); setZoom(nz); const nd = { w: W * baseScale * nz, h: H * baseScale * nz }; setOffset((o) => clampOffset(o, nd.w, nd.h)); }}
            data-testid="cropper-zoom" className="flex-1 accent-[#748A3D]"
          />
          <button type="button" onClick={() => setFill((v) => !v)} data-testid="cropper-fill-toggle"
            className={`inline-flex items-center gap-1.5 border font-mono uppercase text-[10px] px-2.5 py-1.5 transition-colors ${fill ? "border-[#748A3D] text-[#CFE0A4]" : "border-[#2E2F31] text-[#8B8D90] hover:text-[#E8E8EA]"}`}>
            {fill ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />} {fill ? "Fill" : "Contain"}
          </button>
        </div>

        {tooSmall && (
          <p data-testid="cropper-too-small" className="mt-3 flex items-start gap-1.5 text-[11px] font-body text-[#E8C46A] leading-snug">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            This image is only {W}×{H}px. It may look soft at small sizes — a larger upload gives a crisper mark.
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onCancel} disabled={busy} data-testid="cropper-cancel"
            className="border border-[#2E2F31] text-[#8B8D90] hover:text-[#E8E8EA] font-mono uppercase text-xs px-3 py-2 transition-colors disabled:opacity-50">Cancel</button>
          <button type="button" onClick={save} disabled={busy || !imgEl} data-testid="cropper-save"
            className="bg-[#57682A] hover:bg-[#748A3D] text-white font-heading font-bold uppercase text-xs px-4 py-2 transition-colors disabled:opacity-50">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ImageCropper;
